import { useEffect, useRef } from 'react';
import {
  type RouteObject,
  To,
  matchRoutes,
  useFetcher,
} from 'react-router-dom';
import { useCurrentUserAuth } from './useCurrentUserAuth';

let appRoutesCache: RouteObject[] | null = null;

// Dynamic import defers evaluation of `../routes` until after the synchronous
// module graph settles, avoiding a circular import via EmbedRoutes ↔
// DefaultLayoutV3 (this hook is reachable from AppSidebar → WorkspaceSidebarIcons
// → PrefetchingLink/PrefetchingNavLink, which sits underneath `routes/index.tsx`
// in the import graph). `require()` did this under CJS; Vite's ESM bundler
// can't convert a bare `require()` call, hence the dynamic import instead.
import('../routes').then(({ appRoutes }) => {
  appRoutesCache = appRoutes;
});

/**
 * `appRoutes` is module-level static, so the answer to "does this path resolve
 * to a route with a loader" is also static. Caching the result avoids a full
 * route-tree walk on every mount of every prefetching link.
 */
const loaderResolutionCache = new Map<string, boolean>();

const pathHasLoader = (path: string): boolean => {
  const cached = loaderResolutionCache.get(path);

  if (cached !== undefined) return cached;

  // Routes may not have finished loading yet (e.g. the very first tick after
  // module init) -- skip prefetching for this call rather than caching a
  // possibly-wrong `false`.
  if (!appRoutesCache) return false;

  const matches = matchRoutes(appRoutesCache, path);

  let result = false;

  if (matches && matches.length > 0) {
    // `fetcher.load()` requires the *target* (leaf) route to have its own
    // loader -- it throws "No route matches URL" otherwise, even if an
    // ancestor layout route (e.g. the root route's trailing-slash redirect)
    // has one. Checking any match in the chain here would make this
    // effectively always true and defeat the safety check.
    const leafMatch = matches.at(-1);
    const isCatchAll = leafMatch?.route.path === '*';

    result = !isCatchAll && leafMatch?.route.loader !== undefined;
  }

  loaderResolutionCache.set(path, result);

  return result;
};

/**
 * `react-router-dom` v6 has a limitation where it throws errors if `useFetcher().load()` is called on a route that doesn't exist or if it doesn't have a loader function.
 *
 * In v7 `<Link prefetch="render" />` fixes this issue (https://reactrouter.com/api/components/Link#prefetch).
 *
 * **TO-DO:** Remove this hook in v7 when `<Link prefetch="render" />` is available.
 *
 * 1. **Current user is present** – Will not attempt prefetch without an authenticated user.
 * 2. **Destination path is non-empty and hasn't just been prefetched** – Will not re-prefetch the same route repeatedly.
 * 3. **A matching route exists for the path** – Uses `matchRoutes(appRoutes, path)` to ensure the route is valid before calling `fetcher.load()`.
 * 4. **The matching route is not the catch-all/404 (`path === '*'`)** – Skips catch-all routes.
 * 5. **The matched leaf route itself has a `loader` function** – An ancestor layout route having a loader isn't enough; `fetcher.load()` throws unless the target route's own loader exists.
 *
 * If any of the above conditions are not met, the hook simply returns and does not call `fetcher.load()`.
 * This behavior ensures safety: it never throws, nor does it interfere with regular link navigation.
 *
 * **Notes:**
 * - Wrapping `fetcher.load()` in try/catch does not prevent errors from invalid route matches.
 * - This hook is meant for proactive, safe route prefetching.
 *
 * @param to - The destination location, just like the `to` prop for `Link`/`NavLink`.
 * @param enabled - Whether the route loader should be prefetched.
 */
export const useRunRouteLoader = (to: To, enabled = true) => {
  // `useFetcher()` returns a new object on every fetcher state transition, so
  // it intentionally NOT listed in the effect's deps below — `fetcher.load`
  // is invoked but the identity isn't load-bearing.
  const fetcher = useFetcher();
  const lastPathRef = useRef<null | string>(null);
  const { currentUser } = useCurrentUserAuth();

  useEffect(() => {
    if (!enabled) return;
    if (!currentUser) return;

    const path = typeof to === 'string' ? to : to.pathname || '';

    if (!path || path === lastPathRef.current) return;

    lastPathRef.current = path;

    if (!pathHasLoader(path)) return;

    fetcher.load(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, enabled, to]);
};
